import { c as _c } from "react/compiler-runtime";
import { useMemo, type ComponentProps } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import type { ApiRepOverview } from "@/generated/models/ApiRepOverview";
import type { ApiCicOverview } from "@/generated/models/ApiCicOverview";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import UserCICStatus from "../utils/user-cic-status/UserCICStatus";
import UserCICTypeIcon from "../utils/user-cic-type/UserCICTypeIcon";
import OverlappingAvatars from "@/components/common/OverlappingAvatars";
import { buildRepAvatarItems } from "./buildRepAvatarItems";
import { getContributorLabel, type RepDirection } from "./UserPageRep.helpers";
type MobileTab = "rep" | "identity";
type AvatarItems = ComponentProps<typeof OverlappingAvatars>["items"];
function RepContributorRow(t0) {
  const $ = _c(25);
  const {
    overview,
    contributorLabel,
    repAvatarItems,
    isRepActive,
    onOpenOverviewContributors
  } = t0;
  if (!overview) {
    return null;
  }
  if (overview.contributor_count <= 0) {
    let t1;
    if ($[0] !== overview.contributor_count) {
      t1 = formatNumberWithCommas(overview.contributor_count);
      $[0] = overview.contributor_count;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== contributorLabel || $[3] !== t1) {
      t2 = <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.06] tw-bg-black/10 tw-px-3 tw-py-2.5"><span className="tw-text-xs tw-font-medium tw-text-iron-500">{t1}{" "}{contributorLabel}</span></div>;
      $[2] = contributorLabel;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] !== repAvatarItems) {
    t1 = repAvatarItems.length > 0 && <span className="tw-pointer-events-none tw-flex-shrink-0"><OverlappingAvatars items={repAvatarItems} size="sm" maxCount={3} /></span>;
    $[5] = repAvatarItems;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== overview.contributor_count) {
    t2 = formatNumberWithCommas(overview.contributor_count);
    $[7] = overview.contributor_count;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== contributorLabel || $[10] !== t2) {
    t3 = <span className="tw-truncate tw-text-xs tw-font-medium tw-text-iron-300">{t2} {contributorLabel}</span>;
    $[9] = contributorLabel;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t3) {
    t4 = <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">{t1}{t3}</span>;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const contributorRowContent = t4;
  if (!isRepActive) {
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <ChevronRightIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500" aria-hidden="true" />;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== contributorRowContent) {
      t6 = <div className="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-black/15 tw-px-3 tw-py-2.5 tw-text-left">{contributorRowContent}{t5}</div>;
      $[16] = contributorRowContent;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  let t5;
  if ($[18] !== overview.contributor_count) {
    t5 = formatNumberWithCommas(overview.contributor_count);
    $[18] = overview.contributor_count;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = `View all ${t5} ${contributorLabel}`;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronRightIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500" aria-hidden="true" />;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== contributorRowContent || $[22] !== onOpenOverviewContributors || $[23] !== t6) {
    t8 = <button type="button" onClick={onOpenOverviewContributors} aria-label={t6} className="tw-mt-3 tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-black/15 tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors hover:tw-border-white/[0.14] hover:tw-bg-white/[0.04]">{contributorRowContent}{t7}</button>;
    $[21] = contributorRowContent;
    $[22] = onOpenOverviewContributors;
    $[23] = t6;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
export default function MobileTabCards(t0) {
  const $ = _c(84);
  const {
    activeTab,
    onTabChange,
    overview,
    cicOverview,
    profile,
    repDirection,
    cicAvatarItems,
    onOpenOverviewContributors
  } = t0;
  overview?.contributors.data;
  let t1;
  if ($[0] !== overview?.contributors.data) {
    t1 = buildRepAvatarItems(overview?.contributors.data ?? [], 3, {
      omitHref: true
    });
    $[0] = overview?.contributors.data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const repAvatarItems = t1;
  const isRepActive = activeTab === "rep";
  const fallbackContributorLabel = repDirection === "given" ? "receivers" : "raters";
  let t2;
  if ($[2] !== fallbackContributorLabel || $[3] !== overview || $[4] !== repDirection) {
    t2 = overview ? getContributorLabel(repDirection, overview.contributor_count) : fallbackContributorLabel;
    $[2] = fallbackContributorLabel;
    $[3] = overview;
    $[4] = repDirection;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const contributorLabel = t2;
  const repCardClasses = `tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${isRepActive ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t3 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${isRepActive ? "tw-border-primary-300/90 tw-bg-transparent tw-ring-2 tw-ring-primary-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t4;
  if ($[6] !== isRepActive) {
    t4 = isRepActive && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-primary-300 tw-transition-all tw-duration-300" />;
    $[6] = isRepActive;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t3}>{t4}</span></div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isRepActive) {
    t6 = isRepActive && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-blue-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /></>;
    $[11] = isRepActive;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `tw-relative tw-transition-opacity tw-duration-300 ${isRepActive ? "" : "tw-opacity-40"}`;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">Total Rep</div>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== overview) {
    t9 = overview ? formatNumberWithCommas(overview.total_rep) : "\u2014";
    $[14] = overview;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-primary-400">{t9}</div>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== contributorLabel || $[19] !== isRepActive || $[20] !== onOpenOverviewContributors || $[21] !== overview || $[22] !== repAvatarItems) {
    t11 = <RepContributorRow overview={overview} contributorLabel={contributorLabel} repAvatarItems={repAvatarItems} isRepActive={isRepActive} onOpenOverviewContributors={onOpenOverviewContributors} />;
    $[18] = contributorLabel;
    $[19] = isRepActive;
    $[20] = onOpenOverviewContributors;
    $[21] = overview;
    $[22] = repAvatarItems;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t7) {
    t12 = <div className={t7}>{t8}{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t5 || $[30] !== t6) {
    t13 = <>{t5}{t6}{t12}</>;
    $[28] = t12;
    $[29] = t5;
    $[30] = t6;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const repCardContent = t13;
  let t14;
  if ($[32] !== isRepActive || $[33] !== onTabChange || $[34] !== repCardClasses || $[35] !== repCardContent) {
    t14 = isRepActive ? <div className={repCardClasses}>{repCardContent}</div> : <button type="button" aria-pressed={false} onClick={() => onTabChange("rep")} className={`${repCardClasses} tw-cursor-pointer`}>{repCardContent}</button>;
    $[32] = isRepActive;
    $[33] = onTabChange;
    $[34] = repCardClasses;
    $[35] = repCardContent;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = activeTab === "identity";
  let t16;
  if ($[37] !== onTabChange) {
    t16 = () => onTabChange("identity");
    $[37] = onTabChange;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "identity" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t18 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "identity" ? "tw-border-emerald-300/90 tw-bg-transparent tw-ring-2 tw-ring-emerald-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t19;
  if ($[39] !== activeTab) {
    t19 = activeTab === "identity" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-300 tw-transition-all tw-duration-300" />;
    $[39] = activeTab;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t18 || $[42] !== t19) {
    t20 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t18}>{t19}</span></div>;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== activeTab) {
    t21 = activeTab === "identity" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /></>;
    $[44] = activeTab;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "identity" ? "" : "tw-opacity-40"}`;
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">NIC</div>;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const t24 = cicOverview?.total_cic ?? profile.cic;
  let t25;
  if ($[47] !== t24) {
    t25 = formatNumberWithCommas(t24);
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t25) {
    t26 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-white">{t25}</div>;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = cicOverview?.total_cic ?? profile.cic;
  let t28;
  if ($[51] !== t27) {
    t28 = <span className="tw-h-4 tw-w-4 tw-flex-shrink-0"><UserCICTypeIcon cic={t27} /></span>;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = cicOverview?.total_cic ?? profile.cic;
  let t30;
  if ($[53] !== t29) {
    t30 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-emerald-400"><UserCICStatus cic={t29} /></span>;
    $[53] = t29;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== t28 || $[56] !== t30) {
    t31 = <div className="tw-mt-2.5 tw-flex tw-items-center tw-gap-1.5">{t28}{t30}</div>;
    $[55] = t28;
    $[56] = t30;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== activeTab || $[59] !== cicAvatarItems) {
    t32 = cicAvatarItems.length > 0 && <div className={activeTab === "identity" ? "tw-pointer-events-none desktop-hover:tw-pointer-events-auto" : "tw-pointer-events-none"}><OverlappingAvatars items={cicAvatarItems} size="sm" maxCount={3} /></div>;
    $[58] = activeTab;
    $[59] = cicAvatarItems;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  const t33 = cicOverview?.contributor_count ?? 0;
  let t34;
  if ($[61] !== t33) {
    t34 = formatNumberWithCommas(t33);
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  const t35 = (cicOverview?.contributor_count ?? 0) === 1 ? "rater" : "raters";
  let t36;
  if ($[63] !== t34 || $[64] !== t35) {
    t36 = <span className="tw-text-xs tw-font-normal tw-text-iron-400">{t34}{" "}{t35}</span>;
    $[63] = t34;
    $[64] = t35;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] !== t32 || $[67] !== t36) {
    t37 = <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">{t32}{t36}</div>;
    $[66] = t32;
    $[67] = t36;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== t22 || $[70] !== t26 || $[71] !== t31 || $[72] !== t37) {
    t38 = <div className={t22}>{t23}{t26}{t31}{t37}</div>;
    $[69] = t22;
    $[70] = t26;
    $[71] = t31;
    $[72] = t37;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t15 || $[75] !== t16 || $[76] !== t17 || $[77] !== t20 || $[78] !== t21 || $[79] !== t38) {
    t39 = <button type="button" aria-pressed={t15} onClick={t16} className={t17}>{t20}{t21}{t38}</button>;
    $[74] = t15;
    $[75] = t16;
    $[76] = t17;
    $[77] = t20;
    $[78] = t21;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t14 || $[82] !== t39) {
    t40 = <div className="tw-grid tw-grid-cols-2 tw-gap-3">{t14}{t39}</div>;
    $[81] = t14;
    $[82] = t39;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  return t40;
}